import { bind } from '@react-rxjs/core';
import { createSignal, partitionByKey } from '@react-rxjs/utils';
import { useEffect, useState } from 'react';
import {
  catchError,
  EMPTY,
  filter,
  from,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { request } from '../../services/request';
import { Coordinate, Location } from '../../types';

const cache = new Map<string, Location>();

const [observer$, onObserve] = createSignal<IntersectionObserverEntry[]>();

const [intersectionByTarget] = partitionByKey(
  observer$.pipe(mergeMap((value) => from(value))),
  (value) => value.target
);

export const observer = new window.IntersectionObserver(onObserve, {
  root: null,
  rootMargin: '0px 0px 100px 0px',
  threshold: 0,
});

export const useLocation = (coordinate: Coordinate) => {
  const [target, setRef] = useState<HTMLElement | null>(null);
  const location = useLocationResponse(coordinate, target);

  useEffect(() => {
    if (!target) {
      return;
    }

    observer.observe(target);
    return () => {
      observer.unobserve(target);
    };
  }, [target]);

  return { setRef, location };
};

export const [useLocationResponse] = bind(
  ({ latitude, longitude }: Coordinate, target: HTMLElement | null) => {
    const coordString = `${latitude},${longitude}`;

    if (cache.has(coordString)) {
      return of(cache.get(coordString));
    }

    if (!target) {
      return EMPTY;
    }

    return intersectionByTarget(target).pipe(
      filter((value) => value.isIntersecting),
      take(1),
      switchMap(() =>
        request<Location>(
          `/gateway/reverse-geocoding-service/v20160208/addresses/${coordString}`
        ).pipe(
          tap((location) => {
            cache.set(coordString, location);
          }),
          catchError(() => EMPTY)
        )
      )
    );
  },
  null
);
